import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { decryptPayload, parseEncryptedPayload } from '../lib/crypto';
import { auth } from '../lib/firebase';
import { isAdminUser, signInAdmin, signOutAdmin, signUpAdmin } from '../services/adminAuthService';
import { fetchInquiries } from '../services/inquiryService';
import './AdminPage.css';

function formatDate(date) {
  if (!date) return '-';
  return new Intl.DateTimeFormat('ko-KR', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

function formatDayLabel(date) {
  return `${date.getMonth() + 1}/${date.getDate()}`;
}

function startOfDay(date) {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
}

function buildDailyCounts(inquiries, days = 14) {
  const today = startOfDay(new Date());
  const buckets = [];

  for (let i = days - 1; i >= 0; i -= 1) {
    const day = new Date(today);
    day.setDate(today.getDate() - i);
    buckets.push({
      key: day.toISOString().slice(0, 10),
      label: formatDayLabel(day),
      date: day,
      count: 0,
    });
  }

  const index = new Map(buckets.map((item, idx) => [item.key, idx]));

  inquiries.forEach((item) => {
    if (!item.createdAt) return;
    const key = startOfDay(item.createdAt).toISOString().slice(0, 10);
    if (index.has(key)) {
      buckets[index.get(key)].count += 1;
    }
  });

  return buckets;
}

function RegistrationChart({ data }) {
  const width = 720;
  const height = 260;
  const padding = { top: 24, right: 20, bottom: 36, left: 36 };
  const maxCount = Math.max(1, ...data.map((item) => item.count));
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  const points = data.map((item, index) => {
    const x =
      padding.left +
      (data.length === 1 ? chartWidth / 2 : (index / (data.length - 1)) * chartWidth);
    const y = padding.top + chartHeight - (item.count / maxCount) * chartHeight;
    return { ...item, x, y };
  });

  const linePath = points
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
    .join(' ');

  const areaPath = [
    `M ${points[0].x} ${padding.top + chartHeight}`,
    ...points.map((point) => `L ${point.x} ${point.y}`),
    `L ${points[points.length - 1].x} ${padding.top + chartHeight}`,
    'Z',
  ].join(' ');

  const yTicks = [0, Math.ceil(maxCount / 2), maxCount];

  return (
    <div className="dash-chart">
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="DB 등록수 그래프">
        <defs>
          <linearGradient id="registerFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#4f63f6" stopOpacity="0.28" />
            <stop offset="100%" stopColor="#4f63f6" stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {yTicks.map((tick) => {
          const y = padding.top + chartHeight - (tick / maxCount) * chartHeight;
          return (
            <g key={tick}>
              <line
                x1={padding.left}
                y1={y}
                x2={width - padding.right}
                y2={y}
                stroke="#edf1f7"
                strokeWidth="1"
              />
              <text x={padding.left - 10} y={y + 4} textAnchor="end" className="dash-chart__axis">
                {tick}
              </text>
            </g>
          );
        })}

        <path d={areaPath} fill="url(#registerFill)" />
        <path d={linePath} fill="none" stroke="#4f63f6" strokeWidth="3" strokeLinecap="round" />

        {points.map((point) => (
          <g key={point.key}>
            <circle cx={point.x} cy={point.y} r="4.5" fill="#fff" stroke="#4f63f6" strokeWidth="2.5" />
            <text
              x={point.x}
              y={height - 12}
              textAnchor="middle"
              className="dash-chart__axis"
            >
              {point.label}
            </text>
            {point.count > 0 ? (
              <text
                x={point.x}
                y={point.y - 10}
                textAnchor="middle"
                className="dash-chart__value"
              >
                {point.count}
              </text>
            ) : null}
          </g>
        ))}
      </svg>
    </div>
  );
}

function AdminAuthForm() {
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [adminCode, setAdminCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const resetMessages = () => setError('');

  const switchMode = (nextMode) => {
    setMode(nextMode);
    resetMessages();
    setPassword('');
    setPasswordConfirm('');
    setAdminCode('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    resetMessages();

    if (mode === 'signup' && password !== passwordConfirm) {
      setError('비밀번호 확인이 일치하지 않습니다.');
      return;
    }

    if (mode === 'signup' && password.length < 8) {
      setError('비밀번호는 8자 이상 입력해 주세요.');
      return;
    }

    setSubmitting(true);
    try {
      if (mode === 'login') {
        await signInAdmin({ email, password });
      } else {
        await signUpAdmin({ email, password, adminCode });
      }
    } catch (err) {
      setError(err.message || '요청 처리 중 오류가 발생했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="admin-auth">
      <div className="admin-card">
        <p className="admin-card__eyebrow">i-LOG Admin</p>
        <h1>{mode === 'login' ? '관리자 로그인' : '관리자 회원가입'}</h1>
        <p className="admin-card__lead">
          {mode === 'login'
            ? '등록된 관리자 계정으로 로그인하세요.'
            : '관리자 코드를 입력하면 계정을 생성할 수 있습니다.'}
        </p>

        <div className="admin-tabs" role="tablist" aria-label="관리자 인증">
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'login'}
            className={mode === 'login' ? 'is-active' : ''}
            onClick={() => switchMode('login')}
          >
            로그인
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'signup'}
            className={mode === 'signup' ? 'is-active' : ''}
            onClick={() => switchMode('signup')}
          >
            회원가입
          </button>
        </div>

        <form onSubmit={handleSubmit} className="admin-form">
          <label>
            이메일
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              required
              disabled={submitting}
            />
          </label>

          <label>
            비밀번호
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              required
              minLength={mode === 'signup' ? 8 : undefined}
              disabled={submitting}
            />
          </label>

          {mode === 'signup' ? (
            <>
              <label>
                비밀번호 확인
                <input
                  type="password"
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                  autoComplete="new-password"
                  required
                  disabled={submitting}
                />
              </label>
              <label>
                관리자 코드
                <input
                  type="password"
                  value={adminCode}
                  onChange={(e) => setAdminCode(e.target.value)}
                  placeholder="ADMIN2026"
                  required
                  disabled={submitting}
                />
              </label>
            </>
          ) : null}

          {error ? <p className="admin-error">{error}</p> : null}

          <button type="submit" className="btn btn--primary" disabled={submitting}>
            {submitting ? '처리 중…' : mode === 'login' ? '로그인' : '회원가입'}
          </button>
        </form>

        <Link to="/" className="admin-back">
          ← 랜딩으로
        </Link>
      </div>
    </div>
  );
}

function AdminDashboard({ user }) {
  const [activeMenu, setActiveMenu] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [inquiries, setInquiries] = useState([]);
  const [decrypted, setDecrypted] = useState({});
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState('');

  const loadInquiries = useCallback(async () => {
    setLoading(true);
    setListError('');
    try {
      const rows = await fetchInquiries();
      setInquiries(rows);

      const secret = process.env.REACT_APP_INQUIRY_ENCRYPTION_KEY;
      if (!secret) {
        setDecrypted({});
        return;
      }

      const next = {};
      await Promise.all(
        rows.map(async (item) => {
          try {
            const encrypted = parseEncryptedPayload(item.encryptedPayload);
            next[item.id] = await decryptPayload(encrypted, secret);
          } catch {
            next[item.id] = null;
          }
        })
      );
      setDecrypted(next);
    } catch (err) {
      setListError(err.message || '문의 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInquiries();
  }, [loadInquiries]);

  const dailyCounts = useMemo(() => buildDailyCounts(inquiries, 14), [inquiries]);

  const stats = useMemo(() => {
    const today = startOfDay(new Date());
    const weekAgo = new Date(today);
    weekAgo.setDate(today.getDate() - 6);

    const todayCount = inquiries.filter(
      (item) => item.createdAt && startOfDay(item.createdAt).getTime() === today.getTime()
    ).length;

    const weekCount = inquiries.filter(
      (item) => item.createdAt && item.createdAt >= weekAgo
    ).length;

    const yesterday = dailyCounts[dailyCounts.length - 2]?.count || 0;
    const todayTrend = todayCount - yesterday;

    return { todayCount, weekCount, todayTrend };
  }, [inquiries, dailyCounts]);

  const handleLogout = async () => {
    await signOutAdmin();
  };

  const closeSidebar = () => setSidebarOpen(false);

  return (
    <div className={`dash${sidebarOpen ? ' dash--sidebar-open' : ''}`}>
      {sidebarOpen ? (
        <button
          type="button"
          className="dash-backdrop"
          aria-label="메뉴 닫기"
          onClick={closeSidebar}
        />
      ) : null}

      <aside className="dash-sidebar">
        <div className="dash-brand">
          <span className="dash-brand__mark">i</span>
          <div>
            <strong>i-LOG</strong>
            <p>Admin Console</p>
          </div>
        </div>

        <nav className="dash-nav" aria-label="관리자 메뉴">
          <button
            type="button"
            className={activeMenu === 'dashboard' ? 'is-active' : ''}
            onClick={() => {
              setActiveMenu('dashboard');
              closeSidebar();
            }}
          >
            <span className="dash-nav__icon" aria-hidden="true">▦</span>
            대시보드
          </button>
          <button
            type="button"
            className={activeMenu === 'inquiries' ? 'is-active' : ''}
            onClick={() => {
              setActiveMenu('inquiries');
              closeSidebar();
            }}
          >
            <span className="dash-nav__icon" aria-hidden="true">☰</span>
            문의 목록
          </button>
        </nav>

        <div className="dash-sidebar__foot">
          <div className="dash-user">
            <span className="dash-user__avatar">{user.email?.[0]?.toUpperCase() || 'A'}</span>
            <div>
              <strong>관리자</strong>
              <p>{user.email}</p>
            </div>
          </div>
          <button type="button" className="btn btn--ghost dash-logout" onClick={handleLogout}>
            로그아웃
          </button>
          <Link to="/" className="dash-site-link" onClick={closeSidebar}>
            사이트 보기
          </Link>
        </div>
      </aside>

      <div className="dash-main">
        <header className="dash-topbar">
          <div className="dash-topbar__left">
            <button
              type="button"
              className="dash-menu-btn"
              aria-label="메뉴 열기"
              onClick={() => setSidebarOpen(true)}
            >
              ☰
            </button>
            <div>
              <p className="dash-topbar__eyebrow">Overview</p>
              <h1>{activeMenu === 'dashboard' ? 'Dashboard' : '문의 목록'}</h1>
            </div>
          </div>
          <div className="dash-topbar__actions">
            <button
              type="button"
              className="btn btn--ghost"
              onClick={loadInquiries}
              disabled={loading}
            >
              {loading ? '불러오는 중…' : '새로고침'}
            </button>
          </div>
        </header>

        {listError ? <p className="admin-error dash-error">{listError}</p> : null}

        {activeMenu === 'dashboard' ? (
          <>
            <section className="dash-metrics">
              <article className="dash-metric">
                <div>
                  <span>전체 DB 등록</span>
                  <strong>{inquiries.length}</strong>
                </div>
                <p className="dash-metric__hint">누적 문의 건수</p>
                <div className="dash-spark dash-spark--blue" aria-hidden="true" />
              </article>
              <article className="dash-metric">
                <div>
                  <span>오늘 등록</span>
                  <strong>{stats.todayCount}</strong>
                </div>
                <p className={`dash-metric__trend ${stats.todayTrend >= 0 ? 'is-up' : 'is-down'}`}>
                  {stats.todayTrend >= 0 ? '▲' : '▼'} 전일 대비 {Math.abs(stats.todayTrend)}
                </p>
                <div className="dash-spark dash-spark--green" aria-hidden="true" />
              </article>
              <article className="dash-metric">
                <div>
                  <span>최근 7일</span>
                  <strong>{stats.weekCount}</strong>
                </div>
                <p className="dash-metric__hint">주간 신규 등록</p>
                <div className="dash-spark dash-spark--pink" aria-hidden="true" />
              </article>
            </section>

            <section className="dash-panel">
              <div className="dash-panel__head">
                <div>
                  <h2>DB 등록수</h2>
                  <p>최근 14일 문의 등록 추이</p>
                </div>
                <span className="dash-chip">Day</span>
              </div>
              <RegistrationChart data={dailyCounts} />
            </section>

            <section className="dash-panel">
              <div className="dash-panel__head">
                <div>
                  <h2>최근 문의</h2>
                  <p>최신 접수 내역</p>
                </div>
                <button
                  type="button"
                  className="dash-link-btn"
                  onClick={() => setActiveMenu('inquiries')}
                >
                  전체 보기
                </button>
              </div>
              <InquiryTable
                inquiries={inquiries.slice(0, 6)}
                decrypted={decrypted}
                loading={loading}
              />
            </section>
          </>
        ) : (
          <section className="dash-panel">
            <div className="dash-panel__head">
              <div>
                <h2>전체 문의 목록</h2>
                <p>총 {inquiries.length}건</p>
              </div>
            </div>
            <InquiryTable inquiries={inquiries} decrypted={decrypted} loading={loading} />
          </section>
        )}
      </div>
    </div>
  );
}

function InquiryTable({ inquiries, decrypted, loading }) {
  if (loading && inquiries.length === 0) {
    return <p className="dash-empty">불러오는 중…</p>;
  }

  if (!loading && inquiries.length === 0) {
    return <p className="dash-empty">접수된 문의가 없습니다.</p>;
  }

  return (
    <div className="dash-table-wrap">
      <table className="dash-table">
        <thead>
          <tr>
            <th>접수일시</th>
            <th>이름</th>
            <th>전화번호</th>
            <th>주소</th>
            <th>상태</th>
          </tr>
        </thead>
        <tbody>
          {inquiries.map((item) => {
            const data = decrypted[item.id];
            return (
              <tr key={item.id}>
                <td>{formatDate(item.createdAt)}</td>
                <td>{data?.name || '—'}</td>
                <td>
                  {data?.phone ? (
                    <a href={`tel:${data.phone}`}>{data.phone}</a>
                  ) : (
                    '—'
                  )}
                </td>
                <td className="dash-table__address">{data?.address || '—'}</td>
                <td>
                  <span className={`dash-status ${data ? 'is-ok' : 'is-pending'}`}>
                    {data ? '확인됨' : '처리중'}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function AdminPage() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (nextUser) => {
      if (!nextUser) {
        setUser(null);
        setAuthorized(false);
        setAuthLoading(false);
        return;
      }

      try {
        const allowed = await isAdminUser(nextUser.uid);
        if (!allowed) {
          await signOutAdmin();
          setUser(null);
          setAuthorized(false);
        } else {
          setUser(nextUser);
          setAuthorized(true);
        }
      } catch {
        setUser(null);
        setAuthorized(false);
      } finally {
        setAuthLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  if (authLoading) {
    return <div className="admin-page admin-page--center">로딩 중…</div>;
  }

  if (!(user && authorized)) {
    return (
      <main className="admin-page admin-page--auth">
        <AdminAuthForm />
      </main>
    );
  }

  return (
    <main className="admin-page admin-page--dash">
      <AdminDashboard user={user} />
    </main>
  );
}

export default AdminPage;
